import React, { useEffect, useState, useRef } from "react";
import Modal from "react-modal";
import moment from "moment";
import { useSelector } from "react-redux";
import { DateRangePicker } from "rsuite";
import crossImage from "../../images/singleinvoice/cross-red.svg";
import { ButtonSkeleton } from "./CalendarComponents/SkeletonLoader.jsx";
import CalendarIcon from "../ui/CalendarIcon";

const ImportDateRangeModal = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    dateRange,
    onDateRangeChange,
    currentViewDateRange,
    isImportLoading = false
}) => {
    const { appSettings } = useSelector((state) => state.appSettings);
    const [localRange, setLocalRange] = useState(null);
    const hasInitializedRef = useRef(false);

    useEffect(() => {
        if (isOpen && !hasInitializedRef.current) {
            let initialStartDate;
            let initialEndDate;

            if (dateRange && dateRange.startDate && dateRange.endDate) {
                initialStartDate = new Date(dateRange.startDate);
                initialEndDate = new Date(dateRange.endDate);
            } else if (currentViewDateRange?.startDate && currentViewDateRange?.endDate) {
                initialStartDate = new Date(currentViewDateRange.startDate);
                initialEndDate = new Date(currentViewDateRange.endDate);
            } else {
                const today = new Date();
                initialStartDate = today;
                initialEndDate = today;
            }

            setLocalRange([initialStartDate, initialEndDate]);
            hasInitializedRef.current = true;
        } else if (!isOpen) {
            // Modal closed; reset initialization and clear local range
            hasInitializedRef.current = false;
            setLocalRange(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleDateRangeChange = (range) => {
        // rsuite DateRangePicker returns [startDate, endDate] or null
        if (!range || !Array.isArray(range) || !range[0] || !range[1]) {
            return;
        }

        const adjustedStartDate = new Date(range[0]);
        const adjustedEndDate = new Date(range[1]);

        // Update local state so the picker display follows the selection
        setLocalRange([adjustedStartDate, adjustedEndDate]);

        if (onDateRangeChange) {
            onDateRangeChange({
                startDate: adjustedStartDate,
                endDate: adjustedEndDate,
            });
        }
    };

    const handleConfirm = () => {
        if (!localRange || !localRange[0] || !localRange[1]) {
            alert('Please select a date range');
            return;
        }

        const startDate = localRange[0];
        const endDate = localRange[1];

        if (moment(endDate).isBefore(moment(startDate))) {
            alert('End date must be after start date');
            return;
        }
        onConfirm({ startDate, endDate });
    };

    return (
        <Modal
            className="import-events-modal import-events-modal-new"
            isOpen={isOpen}
            onRequestClose={() => {
                if (!isImportLoading) {
                    onClose();
                }
            }}
            contentLabel="Import Events to Time Tracker"
            style={{
                content: {
                    height: 'auto',
                    width: '442px',
                    maxWidth: '90vw',
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    overflowY: 'auto',
                    maxHeight: '90vh',
                    padding: '32px 40px 42px 42px',
                    borderRadius: '15px',
                    background: '#F9F9F9',
                    border: 'none',
                    boxShadow: '0px 4px 60px rgba(0,0,0,0.12)',
                },
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '500', color: '#282828', lineHeight: '1.3' }}>
                    Import Events to Time Tracker
                </h2>
                <a
                    onClick={(e) => {
                        if (!isImportLoading) {
                            e.preventDefault();
                            onClose();
                        }
                    }}
                    style={{
                        cursor: isImportLoading ? 'not-allowed' : 'pointer',
                        opacity: isImportLoading ? 0.5 : 1,
                        pointerEvents: isImportLoading ? 'none' : 'auto',
                        padding: '4px',
                        marginLeft: '16px',
                        flexShrink: 0,
                        marginTop: '2px',
                    }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <path d="M0.353516 12.3535L12.3535 0.353516" stroke="black"/>
                        <path d="M0.353516 0.353515L12.3535 12.3535" stroke="black"/>
                    </svg>
                </a>
            </div>

            <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#6A7988', fontSize: '17px', fontWeight: '300', marginBottom: '7px' }}>
                    Date Range
                </label>
                <div className="date-picker-outer date-picker-outer-all">
                    <div className="custoom-icon-calender custoom-icon-calender-all">
                        <DateRangePicker
                            showOneCalendar
                            id="dateRangeInput"
                            format="dd/MM/yyyy"
                            character="   to   "
                            placeholder="Select Date Range"
                            value={localRange}
                            onChange={handleDateRangeChange}
                            onOk={handleDateRangeChange}
                            placement="auto"
                            isoWeek={appSettings?.weekStartDay === "Monday"}
                            caretAs={CalendarIcon}
                        />
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: '0' }}>
                <p style={{ margin: '0 0 16px 0', fontSize: '13px', fontWeight: '300', color: '#282828', lineHeight: '1.6' }}>
                    This will load all <strong style={{ fontWeight: '500' }}>Events</strong> into the time tracker. You'll be able to edit your selections on the next screen.
                </p>
                <p style={{ margin: 0, fontSize: '13px', fontWeight: '300', color: '#282828', lineHeight: '1.6' }}>
                    Please note that this import does not include events tagged as Holidays, Anniversaries, Birthdays, or Time-Off.
                </p>
            </div>

            <div className="sprint-btn-outer" style={{ marginTop: '40px' }}>
                <button
                    type="button"
                    className="submit-client cancel-sprint"
                    onClick={(e) => {
                        if (!isImportLoading) {
                            e.preventDefault();
                            onClose();
                        }
                    }}
                    disabled={isImportLoading}
                    style={{ cursor: isImportLoading ? 'not-allowed' : 'pointer', opacity: isImportLoading ? 0.5 : 1 }}
                >
                    {isImportLoading ? <ButtonSkeleton width="60px" height="18px" /> : 'Cancel'}
                </button>
                <button
                    type="button"
                    className="submit-client save-sprint"
                    onClick={(e) => {
                        if (!isImportLoading) {
                            e.preventDefault();
                            handleConfirm();
                        }
                    }}
                    disabled={isImportLoading}
                    style={{ cursor: isImportLoading ? 'not-allowed' : 'pointer', opacity: isImportLoading ? 0.5 : 1 }}
                >
                    {isImportLoading ? <ButtonSkeleton width="120px" height="18px" /> : 'Confirm Import'}
                </button>
            </div>
        </Modal>
    );
};

export default ImportDateRangeModal;



